import { useCallback, useEffect, useState } from "react";
import type { Guest } from "@/types/Guest";
import type { GuestDataUpdatedDetail } from "@/providers/GuestsProvider";
import type { GuestReconciledDetail } from "@/lib/guest-reconcile/reconcileScheduler";
import { mergeGuestPatch } from "@/lib/utils/guest-patch";

/**
 * Subscribe to the confirmed save patch for ONE guest.
 *
 * `guestDataUpdated` is dispatched by {@link GuestsProvider}'s `updateGuest`
 * AFTER the write came back clean, so a patch here is server truth, never an
 * optimistic value (the inline/bulk lanes deliberately do not dispatch).
 *
 * Both the popup and the companion sub-pane render a guest object their opener
 * captured once, so without this they keep rendering pre-save values — the
 * consent warning in the send-to-guest dialog being the visible one. Guarded on
 * `patch` as well as `guestId`/`eventId`: `mergeGuestPatch` destructures its
 * argument, and a throw inside a setState updater would take the whole popup
 * (and its unsaved edits) down with it.
 *
 * `onPatch` must be stable — pass a `useCallback` or a functional setState.
 */
export function useGuestDataUpdated(
  guestId: string | undefined,
  eventId: string | undefined,
  onPatch: (patch: Partial<Guest>) => void,
): void {
  useEffect(() => {
    if (!guestId || !eventId) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<GuestDataUpdatedDetail>).detail;
      if (!detail?.patch || detail.guestId !== guestId) return;
      if (detail.eventId !== eventId) return;
      onPatch(detail.patch);
    };
    window.addEventListener("guestDataUpdated", handler);
    return () => window.removeEventListener("guestDataUpdated", handler);
  }, [guestId, eventId, onPatch]);
}

/**
 * Subscribe to the fresh guest a workflow reconcile read back.
 *
 * An `UPDATE_GUEST` automation runs async on the consumer, so it can overrule
 * what was just saved. Merged rather than replaced: the reconcile projection is
 * narrower than what a pane holds, so a replace blanks the rest.
 */
function useGuestReconciled(
  guestId: string | undefined,
  eventId: string | undefined,
  onFresh: (fresh: Guest) => void,
): void {
  useEffect(() => {
    if (!guestId || !eventId) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<GuestReconciledDetail>).detail;
      if (!detail?.guest || detail.guestId !== guestId) return;
      if (detail.eventId !== eventId) return;
      onFresh(detail.guest);
    };
    window.addEventListener("guestReconciled", handler);
    return () => window.removeEventListener("guestReconciled", handler);
  }, [guestId, eventId, onFresh]);
}

/**
 * A guest object that follows its own confirmed saves and the automations that
 * act on them.
 *
 * For surfaces that hold a captured guest and have no refetch of their own —
 * the companion sub-pane inside the detail popup, which is a second
 * `GuestDetailBody` fed from the companions list and, unlike the popup itself,
 * has no 20s poll to eventually paper over the staleness. That also makes the
 * reconcile lane load-bearing here rather than a nicety: nothing else would
 * ever correct a value an automation overruled. Pass `null` while the surface
 * is closed; the copy re-seeds whenever the opener hands in a different guest.
 */
export function useLiveGuestPatches(
  guest: Guest | null,
  eventId: string | undefined,
): Guest | null {
  const [live, setLive] = useState<Guest | null>(guest);
  useEffect(() => {
    setLive(guest);
  }, [guest]);

  const apply = useCallback((patch: Partial<Guest>) => {
    setLive((prev) => (prev ? mergeGuestPatch(prev, patch) : prev));
  }, []);
  const applyFresh = useCallback((fresh: Guest) => {
    setLive((prev) => (prev ? { ...prev, ...fresh } : prev));
  }, []);

  useGuestDataUpdated(guest?.guestId, eventId, apply);
  useGuestReconciled(guest?.guestId, eventId, applyFresh);

  return live;
}
